import { FC, useCallback, useEffect } from 'react';

import analytics from '@repo/analytics';
import { DesignSortType } from '@repo/api-client';
import { isMobileOperatingSystem } from '@repo/detection';
import { Divider } from '@repo/editor/src/components/utilities/styles';
import {
  PROJECT_LIST_MODAL_PROJECT_CLICK,
  PROJECT_LIST_MODAL_SHOW,
} from '@repo/editor/src/global/analytics/events';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { Design } from '@repo/editor/src/types';
import {
  DESIGN_PARAM,
  NEW_DESIGN_TEMPLATE_PARAM,
  TEMPLATE_PARAM,
  WORKSPACE_PARAM,
} from '@repo/editor/src/utils/window';
import { withEventScope } from '@repo/event-processor';
import { useIntersectionObserver } from '@repo/react-hooks/useIntersectionObserver';
import { Button, secondaryTheme } from '@repo/ui/components/Buttons';
import { SpinnerWrapper } from '@repo/ui/components/Icon';
import LoadingIcon from '@repo/ui/components/Icon/LoadingIcon';
import PremiumBadgeIcon from '@repo/ui/components/Icon/PremiumBadgeIcon';
import { H2, P1, P2, P3 } from '@repo/ui/components/Typography';
import { Modal } from '@repo/ui/deprecated/Modal';
import { themeStyle } from '@repo/ui/theming';
import {
  Drawer,
  DrawerSnapPoints,
} from '@repo/ui-kit/components/modules/Drawer';
import { buildEditorUrl } from '@repo/utils/url';

import { USER_DASHBOARD_TAKE } from 'global/config';
import { useActiveWorkspaceId } from 'global/utils/workspace/useActiveWorkspaceId';
import { FlexColumn } from 'styles/layouts';

import ProjectItem from './ProjectItem/ProjectItem';
import {
  Body,
  DividerWithCenteredText,
  Footer,
  Header,
  Wrapper,
} from './style';
import { ProjectListModalProps } from './types';

/**
 *  This component displays the list of projects of a user and allows them to add an object to an existing project or upgrade to a higher plan.
 */
const ProjectListModal: FC<ProjectListModalProps> = (props) => {
  const { width = '528px', hideFooter = false } = props;

  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );

  const projects: Design[] = useDesignsStore(
    designsStoreSelector.designElements
  );

  const { isOpen, closeModal, addToExistingDesignParams } = useDesignsStore(
    designsStoreSelector.projectListModalProps
  );
  const onClose = closeModal;
  const user = useUserStore(userStoreSelector.user);
  const designsFetch = useDesignsStore(designsStoreSelector.fetchDesigns);
  const workspaceId = useActiveWorkspaceId();

  const isMobile = isMobileOperatingSystem();

  const fetchProjects = useCallback(
    async (refresh?: boolean) => {
      await designsFetch({
        take: USER_DASHBOARD_TAKE,
        sort: DesignSortType.NAME,
        folderId: 'all',
        refresh,
        workspaceId,
      });
    },
    [designsFetch, workspaceId]
  );

  const { ref: loaderRef, inView: loaderVisible } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '0px',
    root: null,
  });

  useEffect(() => {
    if (loaderVisible) {
      fetchProjects();
    }
  }, [loaderVisible, fetchProjects]);

  const projectCount = user?.designsCount ?? 0;
  const projectLimit = user?.designsLimit ?? 0;
  const planText = user?.plan === 'PRO' ? 'Pro' : 'Free';

  const { heading, subHeading } = {
    heading: `Where would you like to open it?`,
    subHeading:
      projectCount > projectLimit
        ? `Great job! You've reached the limit of projects available under the ${planText} Plan. To continue, add to an existing project.`
        : `Great job! You've created ${projectCount} out of ${projectLimit} projects available under the ${planText} Plan. To continue, add to an existing project.`,
  };

  useEffect(() => {
    if (isOpen) {
      analytics.track(PROJECT_LIST_MODAL_SHOW);
      fetchProjects(true);
    }
  }, [isOpen, fetchProjects]);

  const handleProjectClick = (id: string) => {
    if (!addToExistingDesignParams) {
      return;
    }
    analytics.track(PROJECT_LIST_MODAL_PROJECT_CLICK, {
      projectCount: user?.designsCount,
    });
    const queryParams = addToExistingDesignParams.split('?')[1];
    const urlParams = new URLSearchParams(queryParams);

    // replace the template param with the add new design template param, instructing the editor to add a new template to an existing project
    const templateParam = urlParams.get(TEMPLATE_PARAM);
    if (templateParam) {
      urlParams.append(NEW_DESIGN_TEMPLATE_PARAM, templateParam);
      urlParams.delete(TEMPLATE_PARAM);
    }

    if (workspaceId) {
      urlParams.append(WORKSPACE_PARAM, workspaceId);
    }

    // add the design id (did) to the query params
    urlParams.append(DESIGN_PARAM, id);
    window.open(buildEditorUrl(`?${urlParams.toString()}`), '_blank');

    onClose();
  };

  const handleUpgradeClick = () => {
    const label =
      user?.plan === 'PRO' ? 'proProjectLimitReached' : 'projectLimitReached';
    showUpgradeModal('noIncentiveUpgrade', { trackingLabel: label });
    onClose();
  };

  const content = (
    <>
      <Body isMobile={isMobile}>
        {projects.length > 0 ? (
          projects.map((item) => (
            <ProjectItem
              key={item.name}
              name={item.name}
              preview={{
                url: item.preview,
                size: { width: 60, height: 60 },
              }}
              onClick={() => handleProjectClick(item.id)}
            />
          ))
        ) : (
          <SpinnerWrapper
            style={{ margin: 'auto' }}
            theme={{ spinnerColor: themeStyle.varBrandMain }}
          >
            <LoadingIcon height="48px" />
          </SpinnerWrapper>
        )}
      </Body>
      <div ref={loaderRef} />
      {!hideFooter && (
        <Footer isMobile={isMobile}>
          <DividerWithCenteredText>
            <Divider />
            <P3 color={themeStyle.varInkLightMedium}>OR</P3>
            <Divider />
          </DividerWithCenteredText>
          <Button
            icon={{
              component: PremiumBadgeIcon,
            }}
            label="Get Unlimited Projects"
            name="upgradeProjectsButton"
            theme={secondaryTheme}
            width="100%"
            onClick={handleUpgradeClick}
          />
        </Footer>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer.Root
        modal
        defaultSnapPoint={DrawerSnapPoints.FULL_OPENED}
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <Drawer.Content padding="8px 0px">
          <Drawer.Header padding="6px 16px 0px 16px" title={heading} />
          <Wrapper>
            <FlexColumn padding="0px 16px 16px 16px">
              <P2>{subHeading}</P2>
            </FlexColumn>
            {content}
          </Wrapper>
        </Drawer.Content>
      </Drawer.Root>
    );
  }

  return (
    <Modal
      consistentVerticalScrollbar
      noOverflowX
      contentWrapperHeight="100%"
      isOpen={Boolean(user?.designsLimitReached) && isOpen}
      width={width}
      onClose={() => onClose()}
    >
      <Wrapper>
        <Header>
          <H2>{heading}</H2>
          <P1>{subHeading}</P1>
        </Header>
        {content}
      </Wrapper>
    </Modal>
  );
};

export default withEventScope(ProjectListModal, { module: 'projectListModal' });
